// Partir un clip del Estudio de Video en dos — ADR-2026-11-02-tocar-un-clip-lleva-el-cursor.
//
// Las dos mitades son el MISMO material cortado: lo que pasaba en el corte no tiene por
// qué pasar dos veces. Antes la mitad derecha copiaba todo (`{ ...clip }`), así que:
//   • heredaba la transición de entrada del clip, y como las dos mitades quedan pegadas,
//     el render la aplicaba justo en el corte (un fundido en medio de la toma);
//   • las capas con animación de entrada y salida (imagen, animación) se iban en el
//     corte con la salida y volvían a entrar con la entrada.
// Ahora la izquierda se queda con la entrada y la derecha con la salida.

/** Lo mínimo que tiene que quedar de cada lado del corte, en segundos. */
export const MINIMO_POR_LADO_S = 0.1;

type ClipPartible = {
  id: string;
  startTime: number;
  duration: number;
  mediaOffset?: number;
  enterAnimation?: string;
  exitAnimation?: string;
  transitionIn?: unknown;
};

/** ¿Se puede partir este clip en el segundo `t` de la timeline? */
export function sePuedePartir(clip: Pick<ClipPartible, 'startTime' | 'duration'>, t: number): boolean {
  const local = t - clip.startTime;
  return local >= MINIMO_POR_LADO_S && local <= clip.duration - MINIMO_POR_LADO_S;
}

/**
 * Las dos mitades del clip cortado en el segundo `t` de la timeline, o null si el corte
 * cae demasiado cerca de una punta. `velocidad` es la del clip: un segundo de timeline
 * acelerado se come más material, así que el `mediaOffset` de la derecha avanza
 * `izquierda × velocidad`.
 */
export function partirClip<T extends ClipPartible>(
  clip: T,
  t: number,
  idDerecha: string,
  velocidad: number,
): [T, T] | null {
  if (!sePuedePartir(clip, t)) return null;
  const izquierda = t - clip.startTime;
  const base = clip.mediaOffset || 0;
  const mitadIzquierda: T = {
    ...clip,
    duration: izquierda,
    ...(clip.exitAnimation !== undefined ? { exitAnimation: 'none' } : {}),
  };
  const mitadDerecha: T = {
    ...clip,
    id: idDerecha,
    startTime: clip.startTime + izquierda,
    duration: clip.duration - izquierda,
    mediaOffset: base + izquierda * velocidad,
    transitionIn: undefined,
    ...(clip.enterAnimation !== undefined ? { enterAnimation: 'none' } : {}),
  };
  return [mitadIzquierda, mitadDerecha];
}

/**
 * Qué clip parte el atajo `S` cuando no hay ninguno seleccionado: el video que está bajo
 * el cursor en la capa de más abajo (la principal). Sin selección, partir un audio o una
 * capa superpuesta sería adivinar; el video principal es lo que se corta casi siempre.
 */
export function videoBajoElCursor<
  C extends { id: string; type: string; startTime: number; duration: number; trackIndex?: number; aiStatus?: string },
>(clips: C[], t: number): C | null {
  const candidatos = clips.filter(
    (c) =>
      (c.type === 'video' || (c.type === 'ai-generated' && c.aiStatus === 'ready')) &&
      sePuedePartir(c, t),
  );
  if (candidatos.length === 0) return null;
  return candidatos.reduce((a, b) => ((b.trackIndex ?? 0) < (a.trackIndex ?? 0) ? b : a));
}
